import React from 'react';
import { motion } from 'framer-motion';

// Swap this when the new profile photo is uploaded (saved to /public/assets).
const PROFILE_IMG = '/assets/newme.png';
const PROFILE_FALLBACK = '/assets/me.png';

const floatingChips = [
  { label: 'Claude', color: '#2bffb0', top: '6%', left: '-4%', delay: 0 },
  { label: 'React · FastAPI', color: '#22e6ff', top: '20%', right: '-8%', delay: 0.6 },
  { label: 'n8n', color: '#ff3df0', bottom: '18%', left: '-8%', delay: 1.1 },
  { label: 'GoHighLevel', color: '#9d5cff', bottom: '4%', right: '-2%', delay: 1.6 },
];

// Niches shown in the hero ticker — full detail lives in #niches.
const nicheTicker = [
  'real estate',
  'home services',
  'solar',
  'SaaS',
  'healthcare',
  'legal & docs',
  'agencies',
  'edtech',
];

const miniFlow = [
  { label: 'Design', color: '#22e6ff' },
  { label: 'Build', color: '#9d5cff' },
  { label: 'Automate', color: '#ff3df0' },
  { label: 'Deploy', color: '#2bffb0' },
];

const HeroSection: React.FC = () => {
  return (
    <section
      id="home"
      className="relative flex min-h-[88vh] flex-col items-center justify-center gap-12 py-16 md:flex-row md:gap-10 md:py-24"
    >
      {/* Left: copy */}
      <motion.div
        className="w-full md:w-[58%]"
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-neon-green/30 bg-neon-green/5 px-3 py-1">
          <span className="live-dot" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-neon-green">
            available for new builds
          </span>
        </div>

        <p className="mb-2 font-mono text-sm text-ink-soft">
          Hi, I&apos;m Chaitanya Sharma —
        </p>
        <h1 className="font-display text-4xl font-bold leading-[1.05] text-ink-main sm:text-5xl lg:text-6xl">
          I build <span className="gradient-text-animated">AI products</span>,
          SaaS and automations that run your business.
        </h1>

        <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-soft">
          AI &amp; Full-Stack Developer and Anthropic{' '}
          <span className="neon-text-green font-semibold">Claude Certified Developer</span>.
          I build production SaaS, AI agents, internal platforms and{' '}
          <span className="neon-text-magenta font-semibold">n8n</span> /{' '}
          <span className="neon-text-cyan font-semibold">GoHighLevel</span>{' '}
          automations with Python, React, FastAPI and modern AI APIs — and I&apos;m
          the founder of{' '}
          <a
            href="https://charyxlabs.tech"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline decoration-dotted underline-offset-4"
            style={{ color: '#d9642c' }}
          >
            Charyx Labs
          </a>
          . I design, build, integrate and deploy the whole system, not just
          one piece of it.
        </p>

        {/* niches ticker */}
        <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <span className="font-mono text-[11px] uppercase tracking-widest text-ink-muted">
            niches:
          </span>
          {nicheTicker.map((n, i) => (
            <React.Fragment key={n}>
              <a
                href="#niches"
                className="font-mono text-[11px] text-ink-soft transition-colors hover:text-neon-cyan"
              >
                {n}
              </a>
              {i < nicheTicker.length - 1 && (
                <span className="font-mono text-[11px] text-ink-muted">·</span>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* mini workflow strip */}
        <div className="mt-8 flex flex-wrap items-center gap-2">
          {miniFlow.map((n, i) => (
            <React.Fragment key={n.label}>
              <span
                className="inline-flex items-center gap-2 rounded-lg border bg-canvas-node px-3 py-1.5 font-mono text-xs"
                style={{ borderColor: `${n.color}66`, color: n.color }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: n.color, boxShadow: `0 0 8px ${n.color}` }}
                />
                {n.label}
              </span>
              {i < miniFlow.length - 1 && (
                <svg width="26" height="12" viewBox="0 0 26 12" fill="none">
                  <path
                    d="M0 6 H20 M20 6 l-5 -4 M20 6 l-5 4"
                    stroke="#6f76a3"
                    strokeWidth="1.4"
                  />
                </svg>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-9 flex flex-wrap gap-4">
          <a href="#projects" className="neon-btn">
            View projects
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
          <a
            href="https://calendly.com/sharmachaitanya945/meeting-with-me"
            target="_blank"
            rel="noopener noreferrer"
            className="neon-btn-ghost"
          >
            Book a free call
          </a>
        </div>

        {/* stat row */}
        <div className="mt-12 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { v: '50+', l: 'workflows shipped' },
            { v: '13+', l: 'businesses supported' },
            { v: '4', l: 'countries served' },
            { v: '100%', l: 'Upwork job success' },
          ].map((s) => (
            <div key={s.l} className="border-l border-canvas-border pl-3">
              <div className="font-display text-2xl font-bold text-ink-main">
                {s.v}
              </div>
              <div className="font-mono text-[11px] uppercase tracking-wide text-ink-muted">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Right: profile node */}
      <motion.div
        className="relative w-full max-w-sm md:w-[42%]"
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <div className="relative mx-auto w-full max-w-[340px]">
          {/* the node frame */}
          <div className="n8n-node animate-glow-pulse p-3">
            <span className="node-port in" />
            <span className="node-port out" />

            {/* node header bar */}
            <div className="mb-3 flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <span className="node-chip !h-7 !w-7 !rounded-lg">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="8" r="4" stroke="#22e6ff" strokeWidth="1.6" />
                    <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke="#22e6ff" strokeWidth="1.6" />
                  </svg>
                </span>
                <span className="font-mono text-xs text-ink-soft">profile.node</span>
              </div>
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-neon-magenta/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-neon-amber/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-neon-green/70" />
              </div>
            </div>

            {/* photo */}
            <div className="relative overflow-hidden rounded-xl border border-canvas-border">
              <img
                src={PROFILE_IMG}
                alt="Chaitanya Sharma"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = PROFILE_FALLBACK;
                }}
                className="h-[340px] w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-canvas-base via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                <span className="rounded-md bg-canvas-base/80 px-2 py-1 font-mono text-[11px] text-neon-cyan backdrop-blur">
                  status: active
                </span>
                <span className="flex items-center gap-1 rounded-md bg-canvas-base/80 px-2 py-1 font-mono text-[11px] text-neon-green backdrop-blur">
                  <span className="live-dot" /> online
                </span>
              </div>
            </div>
          </div>

          {/* floating tool chips */}
          {floatingChips.map((c) => (
            <motion.span
              key={c.label}
              className="absolute z-20 hidden rounded-lg border bg-canvas-node/90 px-3 py-1.5 font-mono text-xs backdrop-blur sm:inline-block"
              style={{
                top: c.top,
                left: c.left,
                right: c.right,
                bottom: c.bottom,
                borderColor: `${c.color}66`,
                color: c.color,
                boxShadow: `0 0 22px -8px ${c.color}`,
              }}
              animate={{ y: [0, -10, 0] }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: c.delay,
                ease: 'easeInOut',
              }}
            >
              {c.label}
            </motion.span>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
